import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Papa from "papaparse";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  CreateExpense,
  Expense,
  ExpenseCategory,
  ExpenseRecurrence,
  ImportExpenseRow,
  MonthlySpend,
  PnlSummary,
} from "@breeze/shared";
import {
  archiveExpenseCategory,
  createExpense,
  createExpenseCategory,
  deleteExpense,
  fetchExpenseCategories,
  fetchExpenses,
  fetchMonthlySpend,
  fetchPnl,
  importExpensesBulk,
  patchExpense,
  restoreExpenseCategory,
} from "../api.js";
import { Section } from "./BusinessGrowth.js";

// ===========================================================================
// Business Growth — Expenses + P&L (Phase 2 + 3)
//
// Owns:
//   1. Recurring outgoings list (manual entry + edit + archive)
//   2. Monthly spend breakdown (recurring projected + one-off CSV imports)
//   3. CSV import flow (4-step modal, MetroBank defaults pre-populated)
//   4. Category manager (add / archive / restore)
//   5. P&L summary (revenue − expenses, ytd, monthly bar)
//
// Auto-roll: the API's getPnl() already merges B_TCLEANER_STATEMENT closed
// payouts into the wages line — no UI work needed here.
// ===========================================================================

type Props = { month: string };

const RECURRENCE_LABEL: Record<ExpenseRecurrence, string> = {
  one_off: "One-off",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
};

function formatGBP(amount: number, opts: { decimals?: 0 | 2 } = {}): string {
  const decimals = opts.decimals ?? 2;
  const abs = Math.abs(amount).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return amount < 0 ? `−£${abs}` : `£${abs}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function monthLabel(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const names = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${names[Number(m[2]) - 1]} ${m[1]}`;
}

const CATEGORY_PALETTE = [
  "#36C5F0", "#ECB22E", "#F07B3F", "#7F6DC5", "#2EB67D",
  "#E01E5A", "#A678A8", "#611F69", "#7E3A7F", "#4A154B",
];

// ---------------------------------------------------------------------------
// Top-level expenses + P&L block
// ---------------------------------------------------------------------------

export function BusinessGrowthExpenses({ month }: Props): JSX.Element {
  const [showRecurring, setShowRecurring] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [showCategories, setShowCategories] = useState(false);
  const [showImport, setShowImport] = useState(false);

  return (
    <>
      <MonthlySpendSection
        month={month}
        onAddRecurring={() => {
          setEditingExpense(null);
          setShowRecurring(true);
        }}
        onManageCategories={() => setShowCategories(true)}
        onImportCsv={() => setShowImport(true)}
      />
      <RecurringList
        onAdd={() => {
          setEditingExpense(null);
          setShowRecurring(true);
        }}
        onEdit={(e) => {
          setEditingExpense(e);
          setShowRecurring(true);
        }}
      />
      <PnlSection />

      {showRecurring && (
        <ExpenseFormModal
          existing={editingExpense}
          onClose={() => {
            setShowRecurring(false);
            setEditingExpense(null);
          }}
        />
      )}
      {showCategories && (
        <CategoryManagerModal onClose={() => setShowCategories(false)} />
      )}
      {showImport && (
        <CsvImportModal onClose={() => setShowImport(false)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Monthly spend breakdown — donut + table for the selected month
// ---------------------------------------------------------------------------

function MonthlySpendSection({
  month,
  onAddRecurring,
  onManageCategories,
  onImportCsv,
}: {
  month: string;
  onAddRecurring: () => void;
  onManageCategories: () => void;
  onImportCsv: () => void;
}): JSX.Element {
  const q = useQuery({
    queryKey: ["growth", "monthlySpend", month],
    queryFn: () => fetchMonthlySpend(month),
  });
  return (
    <Section
      title={`Monthly spend — ${monthLabel(month)}`}
      subtitle="Recurring outgoings (projected) plus one-off bank-imported transactions, by category"
      right={
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onAddRecurring}
            className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight"
          >
            + New expense
          </button>
          <button
            onClick={onImportCsv}
            className="rounded-md border border-ink-500 px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-ink-700"
          >
            Import bank CSV
          </button>
          <button
            onClick={onManageCategories}
            className="rounded-md border border-ink-500 px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-ink-700"
          >
            Categories
          </button>
        </div>
      }
    >
      {q.isLoading ? (
        <div className="text-[15px] text-ink-100">Loading…</div>
      ) : !q.data || q.data.byCategory.length === 0 ? (
        <div className="text-[15px] text-ink-100">
          No expenses recorded for {monthLabel(month)}. Add a recurring outgoing or import a bank CSV.
        </div>
      ) : (
        <SpendBreakdown spend={q.data} />
      )}
    </Section>
  );
}

function SpendBreakdown({ spend }: { spend: MonthlySpend }): JSX.Element {
  const data = spend.byCategory.map((c, i) => ({
    name: c.category,
    value: c.amount,
    color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length],
    manual: c.manualRecurring,
    oneOff: c.oneOff,
  }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Total spend" value={formatGBP(spend.total)} accent="text-stage-lost" />
        <Stat label="Recurring (projected)" value={formatGBP(spend.manualRecurringTotal)} accent="text-ink-50" />
        <Stat label="One-off (CSV)" value={formatGBP(spend.oneOffTotal)} accent="text-ink-50" />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={50}
                outerRadius={100}
                paddingAngle={2}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "#221F27",
                  border: "1px solid #4A4358",
                  color: "#FFFFFF",
                }}
                formatter={(value) => formatGBP(Number(value ?? 0))}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div>
          <table className="w-full text-[15px] text-ink-50">
            <thead>
              <tr className="border-b border-ink-500 text-left text-[13px] uppercase tracking-wider text-ink-100">
                <th className="py-2 pr-3 font-semibold">Category</th>
                <th className="py-2 pr-3 text-right font-semibold">Recurring</th>
                <th className="py-2 pr-3 text-right font-semibold">One-off</th>
                <th className="py-2 pr-3 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.name} className="border-b border-ink-700/60 last:border-b-0">
                  <td className="py-2 pr-3">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block h-3 w-3 rounded-sm"
                        style={{ background: d.color }}
                      />
                      {d.name}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{formatGBP(d.manual)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{formatGBP(d.oneOff)}</td>
                  <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                    {formatGBP(d.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-ink-700 bg-ink-800 p-3">
      <div className="text-[13px] uppercase tracking-[0.14em] text-ink-100">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recurring outgoings list
// ---------------------------------------------------------------------------

function RecurringList({
  onAdd,
  onEdit,
}: {
  onAdd: () => void;
  onEdit: (e: Expense) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["growth", "expenses", "recurring"],
    queryFn: () => fetchExpenses({ recurrence: "recurring_only" }),
  });
  const del = useMutation({
    mutationFn: (id: number) => deleteExpense(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["growth", "expenses"] });
      void qc.invalidateQueries({ queryKey: ["growth", "monthlySpend"] });
      void qc.invalidateQueries({ queryKey: ["growth", "pnl"] });
    },
  });

  return (
    <Section
      title="Recurring outgoings"
      subtitle="Direct debits, standing orders, and yearly bills the dashboard projects forward"
      right={
        <button
          onClick={onAdd}
          className="rounded-md border border-ink-500 px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-ink-700"
        >
          + Add
        </button>
      }
    >
      {q.isLoading ? (
        <div className="text-[15px] text-ink-100">Loading…</div>
      ) : !q.data || q.data.length === 0 ? (
        <div className="text-[15px] text-ink-100">
          None yet. Click "Add" to enter a recurring outgoing (e.g. software subscription, vehicle lease, insurance).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[15px] text-ink-50">
            <thead>
              <tr className="border-b border-ink-500 text-left text-[13px] uppercase tracking-wider text-ink-100">
                <th className="py-2 pr-3 font-semibold">Name</th>
                <th className="py-2 pr-3 font-semibold">Category</th>
                <th className="py-2 pr-3 font-semibold">Recurrence</th>
                <th className="py-2 pr-3 font-semibold">Active from</th>
                <th className="py-2 pr-3 font-semibold">Active to</th>
                <th className="py-2 pr-3 text-right font-semibold">Amount</th>
                <th className="py-2 pr-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((e) => (
                <tr key={e.id} className="border-b border-ink-700/60 last:border-b-0">
                  <td className="py-2 pr-3">
                    <div className="font-semibold">{e.name}</div>
                    {e.notes && <div className="text-[13px] text-ink-100">{e.notes}</div>}
                  </td>
                  <td className="py-2 pr-3">{e.category}</td>
                  <td className="py-2 pr-3">
                    {RECURRENCE_LABEL[e.recurrence]}
                    {e.recurrenceDay && e.recurrence === "monthly"
                      ? ` (${e.recurrenceDay}${ordinalSuffix(e.recurrenceDay)})`
                      : ""}
                  </td>
                  <td className="py-2 pr-3 tabular-nums">{formatDate(e.startsOn)}</td>
                  <td className="py-2 pr-3 tabular-nums">{formatDate(e.endsOn)}</td>
                  <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                    {formatGBP(e.amount)}
                  </td>
                  <td className="py-2 pr-3">
                    <button
                      onClick={() => onEdit(e)}
                      className="rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:bg-ink-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${e.name}"?`)) del.mutate(e.id);
                      }}
                      className="ml-2 rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:bg-ink-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function ordinalSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0]!;
}

// ---------------------------------------------------------------------------
// P&L summary
// ---------------------------------------------------------------------------

function PnlSection(): JSX.Element {
  const q = useQuery({
    queryKey: ["growth", "pnl", 13],
    queryFn: () => fetchPnl(13),
  });
  return (
    <Section title="Profit & Loss" subtitle="Revenue, expenses (incl. wages), and profit per month">
      {q.isLoading ? (
        <div className="text-[15px] text-ink-100">Loading…</div>
      ) : !q.data ? (
        <div className="text-[15px] text-ink-100">No data.</div>
      ) : (
        <PnlBlock pnl={q.data} />
      )}
    </Section>
  );
}

function PnlBlock({ pnl }: { pnl: PnlSummary }): JSX.Element {
  const data = pnl.months.map((p) => ({
    month: monthLabel(p.month),
    revenue: p.revenue,
    expenses: -p.expenses,            // render below zero
    profit: p.profit,
  }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Stat
          label="YTD revenue"
          value={formatGBP(pnl.ytd.revenue, { decimals: 0 })}
          accent="text-stage-won"
        />
        <Stat
          label="YTD expenses"
          value={formatGBP(pnl.ytd.expenses, { decimals: 0 })}
          accent="text-stage-lost"
        />
        <Stat
          label="YTD profit"
          value={formatGBP(pnl.ytd.profit, { decimals: 0 })}
          accent={pnl.ytd.profit >= 0 ? "text-stage-won" : "text-stage-lost"}
        />
        <Stat
          label="YTD margin"
          value={pnl.ytd.marginPct === null ? "—" : `${pnl.ytd.marginPct.toFixed(1)}%`}
          accent="text-ink-50"
        />
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }} stackOffset="sign">
            <CartesianGrid stroke="#332D3A" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="#F0F0F0" tick={{ fill: "#F0F0F0", fontSize: 13 }} />
            <YAxis
              stroke="#F0F0F0"
              tick={{ fill: "#F0F0F0", fontSize: 13 }}
              tickFormatter={(v: number) => formatGBP(v, { decimals: 0 })}
            />
            <Tooltip
              contentStyle={{
                background: "#221F27",
                border: "1px solid #4A4358",
                color: "#FFFFFF",
              }}
              formatter={(value, name) => [
                formatGBP(Math.abs(Number(value ?? 0)), { decimals: 0 }),
                name === "revenue" ? "Revenue" : name === "expenses" ? "Expenses" : "Profit",
              ]}
            />
            <Bar dataKey="revenue" stackId="pnl" fill="#2EB67D" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expenses" stackId="pnl" fill="#E01E5A" radius={[0, 0, 4, 4]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expense form modal (add / edit)
// ---------------------------------------------------------------------------

function ExpenseFormModal({
  existing,
  onClose,
}: {
  existing: Expense | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const cats = useQuery({
    queryKey: ["growth", "categories"],
    queryFn: () => fetchExpenseCategories(false),
  });

  const [name, setName] = useState(existing?.name ?? "");
  const [amount, setAmount] = useState(existing ? String(existing.amount) : "");
  const [category, setCategory] = useState(existing?.category ?? "");
  const [recurrence, setRecurrence] = useState<ExpenseRecurrence>(existing?.recurrence ?? "monthly");
  const [recurrenceDay, setRecurrenceDay] = useState(
    existing?.recurrenceDay ? String(existing.recurrenceDay) : "1"
  );
  const [startsOn, setStartsOn] = useState(existing?.startsOn ?? new Date().toISOString().slice(0, 10));
  const [endsOn, setEndsOn] = useState(existing?.endsOn ?? "");
  const [transactionDate, setTransactionDate] = useState(
    existing?.transactionDate ?? new Date().toISOString().slice(0, 10)
  );
  const [notes, setNotes] = useState(existing?.notes ?? "");

  // Default category to first available once loaded.
  useEffect(() => {
    if (!category && cats.data && cats.data.length > 0) {
      setCategory(cats.data[0]!.name);
    }
  }, [cats.data, category]);

  const save = useMutation({
    mutationFn: async () => {
      const body: CreateExpense = {
        name: name.trim(),
        amount: Number(amount),
        category: category.trim(),
        recurrence,
        recurrenceDay: recurrence === "monthly" ? Number(recurrenceDay) : null,
        startsOn: recurrence === "one_off" ? null : startsOn || null,
        endsOn: recurrence === "one_off" ? null : endsOn || null,
        transactionDate: recurrence === "one_off" ? transactionDate : null,
        notes: notes.trim() || null,
      };
      if (existing) return await patchExpense(existing.id, body);
      return await createExpense(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["growth", "expenses"] });
      void qc.invalidateQueries({ queryKey: ["growth", "monthlySpend"] });
      void qc.invalidateQueries({ queryKey: ["growth", "pnl"] });
      onClose();
    },
  });

  const valid =
    name.trim().length > 0 &&
    Number.isFinite(Number(amount)) &&
    Number(amount) > 0 &&
    category.trim().length > 0;

  return (
    <Modal title={existing ? "Edit expense" : "New expense"} onClose={onClose}>
      <div className="space-y-3 text-[15px]">
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            placeholder="e.g. Cronofy software, vehicle lease"
          />
        </Field>
        <Field label="Amount (£)">
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            placeholder="24.00"
          />
        </Field>
        <Field label="Category">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
          >
            {cats.data?.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Recurrence">
          <select
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value as ExpenseRecurrence)}
            className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="yearly">Yearly</option>
            <option value="one_off">One-off</option>
          </select>
        </Field>
        {recurrence === "monthly" && (
          <Field label="Day of month">
            <input
              value={recurrenceDay}
              onChange={(e) => setRecurrenceDay(e.target.value)}
              type="number"
              min={1}
              max={31}
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </Field>
        )}
        {recurrence !== "one_off" && (
          <>
            <Field label="Starts on">
              <input
                value={startsOn}
                onChange={(e) => setStartsOn(e.target.value)}
                type="date"
                className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
              />
            </Field>
            <Field label="Ends on (optional)">
              <input
                value={endsOn}
                onChange={(e) => setEndsOn(e.target.value)}
                type="date"
                className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
              />
            </Field>
          </>
        )}
        {recurrence === "one_off" && (
          <Field label="Transaction date">
            <input
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              type="date"
              className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </Field>
        )}
        <Field label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
          />
        </Field>
      </div>
      {save.isError && (
        <div className="mt-3 text-[13px] text-stage-lost">
          {(save.error as Error).message}
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700"
        >
          Cancel
        </button>
        <button
          onClick={() => save.mutate()}
          disabled={!valid || save.isPending}
          className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : existing ? "Save changes" : "Add expense"}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Category manager modal
// ---------------------------------------------------------------------------

function CategoryManagerModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["growth", "categories", "all"],
    queryFn: () => fetchExpenseCategories(true),
  });
  const [newName, setNewName] = useState("");
  const create = useMutation({
    mutationFn: () => createExpenseCategory({ name: newName.trim() }),
    onSuccess: () => {
      setNewName("");
      void qc.invalidateQueries({ queryKey: ["growth", "categories"] });
    },
  });
  const archive = useMutation({
    mutationFn: (id: number) => archiveExpenseCategory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["growth", "categories"] }),
  });
  const restore = useMutation({
    mutationFn: (id: number) => restoreExpenseCategory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["growth", "categories"] }),
  });

  return (
    <Modal title="Manage expense categories" onClose={onClose}>
      <p className="text-[15px] text-ink-100">
        Categories appear in the dropdown when adding expenses or importing CSV. Archiving hides
        a category from the picker but keeps existing expense rows intact.
      </p>
      <div className="mt-4 flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New category name"
          className="flex-1 rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
        />
        <button
          onClick={() => create.mutate()}
          disabled={!newName.trim() || create.isPending}
          className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
        >
          Add
        </button>
      </div>
      <ul className="mt-4 max-h-72 overflow-y-auto divide-y divide-ink-700">
        {q.data?.map((c) => (
          <li key={c.id} className="flex items-center justify-between py-2 text-[15px] text-ink-50">
            <span className={c.archivedAt ? "text-ink-100 line-through" : ""}>{c.name}</span>
            {c.archivedAt ? (
              <button
                onClick={() => restore.mutate(c.id)}
                className="rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:bg-ink-700"
              >
                Restore
              </button>
            ) : (
              <button
                onClick={() => archive.mutate(c.id)}
                className="rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:bg-ink-700"
              >
                Archive
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex justify-end">
        <button
          onClick={onClose}
          className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700"
        >
          Done
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// CSV import modal — 4-step flow with MetroBank defaults
// ---------------------------------------------------------------------------
//
// Step 1: Upload — drag/drop or file input. parsed via papaparse client-side.
// Step 2: Map columns (Date / Description / Amount). MetroBank default mapping
//         is pre-populated: Date column header includes "date", Description
//         includes "description" or "narrative", Amount uses "Amount Out" /
//         "Out" preferentially (debits = our outgoings) else "Amount".
// Step 3: Categorize — fuzzy-match each row against existing recurring
//         expenses by name token. Matched rows are pre-tagged green; unmatched
//         rows show a category dropdown defaulting to "other".
// Step 4: Commit — POST to /api/business-growth/expenses/import-bulk.
//
// localStorage caches the user's column mapping per bank so the second import
// from the same MetroBank format skips step 2.

type CsvRow = Record<string, string>;
type ColumnRole = "ignore" | "date" | "description" | "amount";

const LOCALSTORAGE_KEY = "growth.csvMapping.v1";

function detectMetroBankMapping(headers: string[]): Record<string, ColumnRole> {
  // MetroBank statement format (typical):
  //   Date, Reference, Description, Amount In, Amount Out, Balance
  // We default Amount → Amount Out (debits = our expenses). User can flip.
  const mapping: Record<string, ColumnRole> = {};
  for (const h of headers) {
    const lc = h.toLowerCase().trim();
    if (mapping[h]) continue;
    if (lc === "date" || lc === "transaction date" || lc === "posting date") {
      mapping[h] = "date";
    } else if (lc === "amount out" || lc === "out" || lc === "debit") {
      mapping[h] = "amount";
    } else if (
      lc === "description" ||
      lc === "narrative" ||
      lc === "details" ||
      lc === "reference" ||
      lc === "transaction description"
    ) {
      // Prefer "description" over "reference" if both exist — only set if not yet.
      if (!Object.values(mapping).includes("description")) mapping[h] = "description";
      else mapping[h] = "ignore";
    } else {
      mapping[h] = "ignore";
    }
  }
  // Fallback: if we didn't find an "Amount Out" column, use the first column
  // containing "amount".
  if (!Object.values(mapping).includes("amount")) {
    const fallback = headers.find((h) => /amount/i.test(h));
    if (fallback) mapping[fallback] = "amount";
  }
  return mapping;
}

function parseDateGuess(raw: string): string | null {
  // Try common UK formats: DD/MM/YYYY, YYYY-MM-DD, D/M/YY.
  const trimmed = raw.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (iso) return trimmed;
  const uk = /^(\d{1,2})[\/.](\d{1,2})[\/.](\d{2,4})$/.exec(trimmed);
  if (uk) {
    const day = uk[1]!.padStart(2, "0");
    const month = uk[2]!.padStart(2, "0");
    let year = uk[3]!;
    if (year.length === 2) year = "20" + year;
    return `${year}-${month}-${day}`;
  }
  // ISO with time
  const isoT = /^(\d{4})-(\d{2})-(\d{2})T/.exec(trimmed);
  if (isoT) return `${isoT[1]}-${isoT[2]}-${isoT[3]}`;
  return null;
}

function parseAmount(raw: string): number | null {
  // Strip £, commas, parentheses (negative). MetroBank shows debits as positive
  // in "Amount Out" — we keep that as positive for our expense table.
  let s = raw.trim().replace(/[£,]/g, "");
  let neg = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    neg = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith("-")) {
    neg = true;
    s = s.slice(1);
  }
  const n = Number(s);
  if (!Number.isFinite(n) || n === 0) return null;
  return neg ? -n : n;
}

function CsvImportModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const cats = useQuery({
    queryKey: ["growth", "categories"],
    queryFn: () => fetchExpenseCategories(false),
  });
  const recurring = useQuery({
    queryKey: ["growth", "expenses", "recurring"],
    queryFn: () => fetchExpenses({ recurrence: "recurring_only" }),
  });

  type Step = 1 | 2 | 3 | 4;
  const [step, setStep] = useState<Step>(1);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, ColumnRole>>({});
  type StagedRow = {
    transactionDate: string;
    description: string;
    amount: number;
    category: string;
    matchedRecurringId: number | null;
    parseError: string | null;
  };
  const [staged, setStaged] = useState<StagedRow[]>([]);
  const [importResult, setImportResult] = useState<{
    inserted: number;
    matchedToRecurring: number;
    duplicatesSkipped: number;
  } | null>(null);

  function fuzzyMatchRecurring(description: string): number | null {
    if (!recurring.data) return null;
    const desc = description.toLowerCase();
    for (const r of recurring.data) {
      const tokens = r.name.toLowerCase().split(/\s+/).filter(Boolean);
      if (tokens.some((t) => t.length >= 3 && desc.includes(t))) return r.id;
    }
    return null;
  }

  function parseFile(f: File) {
    Papa.parse<CsvRow>(f, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const hdrs = result.meta.fields ?? [];
        setHeaders(hdrs);
        setRows(result.data);
        // Try to recall a saved mapping for these exact headers; otherwise
        // detect from MetroBank-style header names.
        let m: Record<string, ColumnRole>;
        try {
          const cached = localStorage.getItem(LOCALSTORAGE_KEY);
          if (cached) {
            const parsed = JSON.parse(cached) as { headers: string[]; mapping: Record<string, ColumnRole> };
            if (parsed.headers.join("|") === hdrs.join("|")) m = parsed.mapping;
            else m = detectMetroBankMapping(hdrs);
          } else {
            m = detectMetroBankMapping(hdrs);
          }
        } catch {
          m = detectMetroBankMapping(hdrs);
        }
        setMapping(m);
        setStep(2);
      },
    });
  }

  function buildStaged(): StagedRow[] {
    const dateCol = Object.entries(mapping).find(([, r]) => r === "date")?.[0];
    const descCol = Object.entries(mapping).find(([, r]) => r === "description")?.[0];
    const amtCol = Object.entries(mapping).find(([, r]) => r === "amount")?.[0];
    if (!dateCol || !descCol || !amtCol) return [];
    return rows
      .map((row): StagedRow | null => {
        const dateRaw = row[dateCol] ?? "";
        const desc = (row[descCol] ?? "").trim();
        const amtRaw = row[amtCol] ?? "";
        const date = parseDateGuess(dateRaw);
        const amt = parseAmount(amtRaw);
        if (!date || amt === null || amt <= 0 || !desc) return null;
        const matched = fuzzyMatchRecurring(desc);
        return {
          transactionDate: date,
          description: desc,
          amount: amt,
          category: matched
            ? recurring.data?.find((r) => r.id === matched)?.category ?? "other"
            : "other",
          matchedRecurringId: matched,
          parseError: null,
        };
      })
      .filter((x): x is StagedRow => x !== null);
  }

  const importMut = useMutation({
    mutationFn: async () => {
      const sourceFile = file?.name ?? "import.csv";
      const payload: ImportExpenseRow[] = staged.map((s) => ({
        transactionDate: s.transactionDate,
        description: s.description,
        amount: s.amount,
        category: s.category,
        matchedRecurringId: s.matchedRecurringId ?? undefined,
      }));
      return await importExpensesBulk({ sourceFile, rows: payload });
    },
    onSuccess: (r) => {
      setImportResult(r);
      void qc.invalidateQueries({ queryKey: ["growth", "expenses"] });
      void qc.invalidateQueries({ queryKey: ["growth", "monthlySpend"] });
      void qc.invalidateQueries({ queryKey: ["growth", "pnl"] });
      setStep(4);
    },
  });

  // Persist mapping on step transition 2 → 3.
  function continueToCategorize() {
    try {
      localStorage.setItem(
        LOCALSTORAGE_KEY,
        JSON.stringify({ headers, mapping })
      );
    } catch {
      /* ignore */
    }
    setStaged(buildStaged());
    setStep(3);
  }

  return (
    <Modal title="Import bank CSV" wide onClose={onClose}>
      <div className="mb-3 flex items-center gap-2 text-[13px]">
        {([1, 2, 3, 4] as const).map((s) => (
          <span
            key={s}
            className={`rounded-full px-3 py-1 font-semibold ${
              s === step
                ? "bg-slack-aubergine text-ink-50"
                : s < step
                ? "bg-stage-won/30 text-ink-50"
                : "bg-ink-700 text-ink-100"
            }`}
          >
            {s}. {(["Upload", "Map columns", "Categorize", "Done"] as const)[s - 1]}
          </span>
        ))}
      </div>

      {step === 1 && (
        <Step1Upload
          onPick={(f) => {
            setFile(f);
            parseFile(f);
          }}
        />
      )}

      {step === 2 && (
        <Step2Mapping
          headers={headers}
          rows={rows}
          mapping={mapping}
          onChange={setMapping}
          onBack={() => setStep(1)}
          onContinue={continueToCategorize}
        />
      )}

      {step === 3 && (
        <Step3Categorize
          staged={staged}
          setStaged={setStaged}
          categories={cats.data ?? []}
          onBack={() => setStep(2)}
          onCommit={() => importMut.mutate()}
          isPending={importMut.isPending}
          error={importMut.error as Error | null}
        />
      )}

      {step === 4 && importResult && (
        <Step4Done result={importResult} onClose={onClose} />
      )}
    </Modal>
  );
}

function Step1Upload({ onPick }: { onPick: (f: File) => void }): JSX.Element {
  return (
    <div className="space-y-4">
      <p className="text-[15px] text-ink-50">
        Upload a CSV exported from your bank (MetroBank statement format auto-detected).
      </p>
      <input
        type="file"
        accept=".csv,text/csv"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
        }}
        className="block w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
      />
      <p className="text-[13px] text-ink-100">
        Tip: in MetroBank online banking, go to Statements → Download → CSV.
      </p>
    </div>
  );
}

function Step2Mapping({
  headers,
  rows,
  mapping,
  onChange,
  onBack,
  onContinue,
}: {
  headers: string[];
  rows: CsvRow[];
  mapping: Record<string, ColumnRole>;
  onChange: (m: Record<string, ColumnRole>) => void;
  onBack: () => void;
  onContinue: () => void;
}): JSX.Element {
  const ready =
    Object.values(mapping).includes("date") &&
    Object.values(mapping).includes("description") &&
    Object.values(mapping).includes("amount");
  return (
    <div className="space-y-4">
      <p className="text-[15px] text-ink-50">
        Tell us which columns are the date, description, and amount. We pre-filled MetroBank's
        usual layout — adjust if your CSV uses different names.
      </p>
      <div className="overflow-x-auto rounded-md border border-ink-700">
        <table className="w-full text-[13px] text-ink-50">
          <thead>
            <tr className="bg-ink-700/60">
              {headers.map((h) => (
                <th key={h} className="border-r border-ink-700 px-2 py-2 text-left">
                  <div className="text-[13px] font-semibold uppercase tracking-wider text-ink-100">
                    {h}
                  </div>
                  <select
                    value={mapping[h] ?? "ignore"}
                    onChange={(e) =>
                      onChange({ ...mapping, [h]: e.target.value as ColumnRole })
                    }
                    className="mt-1 rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
                  >
                    <option value="ignore">Ignore</option>
                    <option value="date">Date</option>
                    <option value="description">Description</option>
                    <option value="amount">Amount</option>
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 5).map((row, i) => (
              <tr key={i} className="border-t border-ink-700">
                {headers.map((h) => (
                  <td key={h} className="border-r border-ink-700 px-2 py-2">
                    {(row[h] ?? "").slice(0, 80)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700"
        >
          ← Back
        </button>
        <button
          onClick={onContinue}
          disabled={!ready}
          className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

function Step3Categorize({
  staged,
  setStaged,
  categories,
  onBack,
  onCommit,
  isPending,
  error,
}: {
  staged: { transactionDate: string; description: string; amount: number; category: string; matchedRecurringId: number | null; parseError: string | null }[];
  setStaged: React.Dispatch<React.SetStateAction<{ transactionDate: string; description: string; amount: number; category: string; matchedRecurringId: number | null; parseError: string | null }[]>>;
  categories: ExpenseCategory[];
  onBack: () => void;
  onCommit: () => void;
  isPending: boolean;
  error: Error | null;
}): JSX.Element {
  const matchedCount = staged.filter((s) => s.matchedRecurringId !== null).length;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-4 text-[15px] text-ink-50">
        <span><strong>{staged.length}</strong> rows ready</span>
        <span className="text-stage-won">{matchedCount} matched to recurring</span>
        <span className="text-stage-chase">{staged.length - matchedCount} need a category</span>
      </div>
      <div className="max-h-96 overflow-y-auto rounded-md border border-ink-700">
        <table className="w-full text-[13px] text-ink-50">
          <thead className="sticky top-0 bg-ink-700">
            <tr className="text-left text-[13px] uppercase tracking-wider text-ink-100">
              <th className="px-2 py-2 font-semibold">Date</th>
              <th className="px-2 py-2 font-semibold">Description</th>
              <th className="px-2 py-2 text-right font-semibold">Amount</th>
              <th className="px-2 py-2 font-semibold">Category</th>
            </tr>
          </thead>
          <tbody>
            {staged.map((s, i) => (
              <tr
                key={i}
                className={`border-t border-ink-700 ${
                  s.matchedRecurringId ? "bg-stage-won/10" : ""
                }`}
              >
                <td className="px-2 py-1 tabular-nums">
                  {s.transactionDate.split("-").reverse().join("/")}
                </td>
                <td className="px-2 py-1">{s.description}</td>
                <td className="px-2 py-1 text-right tabular-nums">
                  £{s.amount.toFixed(2)}
                </td>
                <td className="px-2 py-1">
                  <select
                    value={s.category}
                    onChange={(e) => {
                      const next = [...staged];
                      next[i] = { ...s, category: e.target.value };
                      setStaged(next);
                    }}
                    className="rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && (
        <div className="text-[13px] text-stage-lost">{error.message}</div>
      )}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700"
        >
          ← Back
        </button>
        <button
          onClick={onCommit}
          disabled={isPending || staged.length === 0}
          className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
        >
          {isPending ? "Importing…" : `Import ${staged.length} rows`}
        </button>
      </div>
    </div>
  );
}

function Step4Done({
  result,
  onClose,
}: {
  result: { inserted: number; matchedToRecurring: number; duplicatesSkipped: number };
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="space-y-4 text-[15px] text-ink-50">
      <p>
        ✅ Imported <strong>{result.inserted}</strong> rows.
        {result.matchedToRecurring > 0 && (
          <> {result.matchedToRecurring} matched to recurring expenses.</>
        )}
        {result.duplicatesSkipped > 0 && (
          <> {result.duplicatesSkipped} duplicates skipped.</>
        )}
      </p>
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal shell
// ---------------------------------------------------------------------------

function Modal({
  title,
  wide,
  onClose,
  children,
}: {
  title: string;
  wide?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-4">
      <div
        className={`w-full ${
          wide ? "max-w-4xl" : "max-w-md"
        } rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card`}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-ink-50">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:bg-ink-700"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="block text-[13px] uppercase tracking-wider text-ink-100">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
